import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import jsQR from 'jsqr';
import { Camera, X, CheckCircle, AlertCircle, UserCheck } from 'lucide-react';
import api from '../../services/api';

function formatWhatsapp(v) {
  const digits = (v || '').replace(/\D/g, '');
  return digits.length > 11 && digits.startsWith('55') ? digits.slice(2) : digits;
}

function formatData(iso) {
  if (!iso) return '';
  // Coluna `date` do Postgres volta como timestamp ISO completo — slice pra pegar só a data.
  return new Date(iso.slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function EventoCheckinScanner() {
  const [searchParams] = useSearchParams();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const intervalRef = useRef(null);

  const [scanning, setScanning] = useState(false);
  const [confirmacao, setConfirmacao] = useState(null);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) buscarPorToken(token);
  }, []);

  async function buscarPorToken(token) {
    setLoading(true); setErro(''); setConfirmacao(null);
    try {
      const { data } = await api.get(`/eventos/checkin/${token}`);
      setConfirmacao(data.data);
    } catch (err) {
      setErro(err?.response?.data?.error || 'QR Code não encontrado no sistema.');
    } finally { setLoading(false); }
  }

  // Localização do celular no momento do check-in (prova de presença no Portal do Corretor).
  // Se a pessoa negar ou o GPS demorar, o check-in segue sem ela.
  function pegarLocalizacao() {
    return new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, precisao: Math.round(p.coords.accuracy) }),
        () => resolve({}),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
      );
    });
  }

  async function confirmarPresenca() {
    setConfirmando(true); setErro('');
    try {
      const gps = await pegarLocalizacao();
      const { data } = await api.post(`/eventos/checkin/${confirmacao.checkin_token}`, gps);
      setConfirmacao(data.data);
    } catch (err) {
      setErro(err?.response?.data?.error || 'Erro ao confirmar check-in.');
    } finally { setConfirmando(false); }
  }

  async function iniciarCamera() {
    setErro(''); setScanning(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
      intervalRef.current = setInterval(lerFrame, 300);
    } catch {
      setErro('Não foi possível acessar a câmera. Verifique as permissões.');
      setScanning(false);
    }
  }

  function pararCamera() {
    clearInterval(intervalRef.current);
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
    setScanning(false);
  }

  function lerFrame() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) return;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);
    if (code?.data) {
      pararCamera();
      try {
        const url = new URL(code.data);
        const token = url.searchParams.get('token');
        if (token) buscarPorToken(token);
        else setErro('QR Code inválido');
      } catch {
        setErro('QR Code inválido');
      }
    }
  }

  useEffect(() => () => pararCamera(), []);

  return (
    <div className="min-h-screen bg-gray-50 p-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-800">Check-in do Evento</h1>
      </div>

      <div className={`bg-black rounded-2xl overflow-hidden mb-4 relative ${scanning ? 'block' : 'hidden'}`} style={{ aspectRatio: '1' }}>
        <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
        <canvas ref={canvasRef} className="hidden" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-48 h-48 border-2 border-white rounded-2xl opacity-70" />
        </div>
      </div>

      {!scanning && !confirmacao && (
        <button onClick={iniciarCamera}
          className="w-full py-5 bg-blue-600 text-white rounded-2xl text-lg font-semibold flex items-center justify-center gap-3 hover:bg-blue-700 active:scale-95 transition-transform">
          <Camera size={28} /> Escanear QR Code
        </button>
      )}

      {scanning && (
        <button onClick={pararCamera}
          className="w-full py-4 border border-gray-300 text-gray-700 rounded-2xl text-base font-medium flex items-center justify-center gap-2 hover:bg-gray-50">
          <X size={20} /> Cancelar
        </button>
      )}

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 mt-2 text-sm">Buscando confirmação...</p>
        </div>
      )}

      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center gap-3 mt-4">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <p className="text-red-700 text-sm">{erro}</p>
        </div>
      )}

      {confirmacao && (
        <div className="bg-white rounded-2xl shadow-lg p-5 space-y-4 mt-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800 leading-tight">{confirmacao.nome}</h2>
              <p className="text-sm text-gray-500">{formatWhatsapp(confirmacao.whatsapp)}</p>
            </div>
            {confirmacao.status === 'compareceu' && (
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-700 shrink-0">Compareceu</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-xl p-3 col-span-2">
              <p className="text-xs text-gray-500 mb-0.5">Evento</p>
              <p className="font-semibold text-gray-800">{confirmacao.evento_nome}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-0.5">Dia</p>
              <p className="font-semibold text-gray-800">{formatData(confirmacao.dia) || '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-0.5">Horário</p>
              <p className="font-semibold text-gray-800">{confirmacao.horario?.slice(0, 5) || '—'}</p>
            </div>
          </div>

          {confirmacao.status === 'cancelado' && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">Essa confirmação foi cancelada.</div>
          )}

          {confirmacao.status === 'compareceu' && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700 flex items-center gap-2">
              <CheckCircle size={16} /> Check-in confirmado{confirmacao.checkin_em ? ` às ${new Date(confirmacao.checkin_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}.
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => { setConfirmacao(null); setErro(''); iniciarCamera(); }}
              className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium flex items-center justify-center gap-2">
              <Camera size={16} /> Escanear outro
            </button>
            {confirmacao.status === 'confirmado' && (
              <button onClick={confirmarPresenca} disabled={confirmando}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-green-700 disabled:opacity-50">
                <UserCheck size={16} /> {confirmando ? 'Confirmando...' : 'Confirmar check-in'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
